import type { ComponentType } from 'react';
import { NavLink, Outlet } from 'react-router';

import { useCurrentContext } from '../../features/identity/hooks/useCurrentContext.js';
import { cx } from '../../shared/lib/cx.js';
import { ErrorState } from '../../shared/ui/ErrorState.js';
import { LoadingState } from '../../shared/ui/LoadingState.js';
import styles from './AppShell.module.css';
import { HeaderMenu } from './HeaderMenu.js';
import { HomeIcon, HouseholdIcon, InvestIcon, PlusIcon, WalletIcon } from './navIcons.js';

/**
 * Bottom navigation, ordered by the PO (2026-08-18). The previous set duplicated itself —
 * "Giao dịch" overlapped the add button and "Báo cáo" overlapped assets — so each slot now
 * owns exactly one job:
 *   Trang chủ · Tài sản (tài sản/tiêu sản/dòng tiền) · [⊕ giao dịch] · Đầu tư · Gia đình
 * Labels use the family's own words — never journal/ledger/subject (skill 13).
 * Split in two arrays because Quick Add owns the raised centre slot between them.
 *
 * "Đầu tư" and "Gia đình" are always present even before either exists: each tab is the
 * only way in to SET one up, so hiding it would leave no entry point (decisions L4/L5).
 * Spec 21 gates the investment module's CONTENT on data, not the doorway. A1 already
 * requires a "Tạo gia đình" action to exist — this tab is where it lives; what A1 hides
 * until then is the household NAME in the header, which still reads "Tài chính cá nhân".
 */
type NavTabProps = { to: string; label: string; Icon: ComponentType; end: boolean };

const LEFT_TABS: NavTabProps[] = [
  { to: '/', label: 'Trang chủ', Icon: HomeIcon, end: true },
  { to: '/tai-san', label: 'Tài sản', Icon: WalletIcon, end: false },
];

const RIGHT_TABS: NavTabProps[] = [
  { to: '/dau-tu', label: 'Đầu tư', Icon: InvestIcon, end: false },
  { to: '/gia-dinh', label: 'Gia đình', Icon: HouseholdIcon, end: false },
];

function NavTab({ to, label, Icon, end }: NavTabProps) {
  return (
    // cx() normalises the `string | undefined` of a CSS Module class for
    // exactOptionalPropertyTypes — see shared/lib/cx.ts.
    <NavLink to={to} end={end} className={cx(styles.navLink)}>
      <span className={styles.navIcon}>
        <Icon />
      </span>
      {label}
    </NavLink>
  );
}

export function AppShell() {
  const state = useCurrentContext();

  if (state.status === 'loading') {
    return <LoadingState label="Đang chuẩn bị không gian tài chính của bạn…" />;
  }

  if (state.status === 'error') {
    return <ErrorState message={state.message} onRetry={state.retry} />;
  }

  const { context } = state;

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        {/* Brand on the left. The wordmark is dark navy, so it keeps the white plate it has
            on the login screen — otherwise it disappears in dark mode. */}
        <img className={styles.brandLogo} src="/brand/finos-logo.png" alt="Personal Finance OS" />

        <div className={styles.headerText}>
          {/* profile.displayName, not selfSubject: this is the person, and it is the name
              the profile screen can actually edit (financial_subjects is RPC-only). */}
          <p className={styles.headerTitle}>{context.profile.displayName}</p>
          {/* The household concept stays hidden until the user creates a family (A1). */}
          <p className={styles.headerSubtitle}>
            {context.isFamily ? context.household.name : 'Tài chính cá nhân'}
          </p>
        </div>

        <HeaderMenu displayName={context.profile.displayName} />
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>

      <nav className={styles.nav} aria-label="Điều hướng chính">
        {LEFT_TABS.map((tab) => (
          <NavTab key={tab.to} {...tab} />
        ))}

        {/* Quick Add is feature number one (spec v2 22.1), so it gets the raised centre
            slot instead of being one tab among equals. Icon-only: aria-label carries the
            accessible name. */}
        <NavLink to="/them" className={cx(styles.navFab)} aria-label="Thêm giao dịch">
          <PlusIcon />
        </NavLink>

        {RIGHT_TABS.map((tab) => (
          <NavTab key={tab.to} {...tab} />
        ))}
      </nav>
    </div>
  );
}
